/**
 * PlatformFlagsContext — global, admin-controlled toggles for the two INDEPENDENT customer
 * modules: SOCIAL (feed / posts / friends / activities) and TOURNAMENTS.
 *
 * Source of truth: GET /public/platform-flags (no auth). Fetched once on mount.
 * Fail CLOSED: both flags default to `false` until the response arrives, and stay `false`
 * on any error — a disabled/unreachable backend must never expose a gated module.
 *
 * - usePlatformFlags()  — hook for function components.
 * - withPlatformFlags() — HOC that injects `platformFlags` for class components.
 * - <ModuleGuard flag>  — route guard: hides while loading, redirects home when disabled.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { apiService } from '@/services/api';

export interface PlatformFlags {
  social: boolean;
  tournaments: boolean;
  /** Global toggle for the "🎵 Request a song" quick action inside Call staff. */
  quick_songs: boolean;
  /** Broker marketplace (mediator groups). */
  marketplace: boolean;
}

export interface PlatformFlagsState extends PlatformFlags {
  /** True once the flags request has resolved (success OR failure). */
  loaded: boolean;
}

const DEFAULT_STATE: PlatformFlagsState = { social: false, tournaments: false, quick_songs: false, marketplace: false, loaded: false };

const PlatformFlagsContext = createContext<PlatformFlagsState>(DEFAULT_STATE);

export const PlatformFlagsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<PlatformFlagsState>(DEFAULT_STATE);

  useEffect(() => {
    let alive = true;
    apiService
      .getPlatformFlags()
      .then((flags) => {
        if (alive) setState({ social: flags.social, tournaments: flags.tournaments, quick_songs: flags.quick_songs, marketplace: flags.marketplace, loaded: true });
      })
      .catch(() => {
        // getPlatformFlags already fails closed, but guard the promise chain too.
        if (alive) setState({ social: false, tournaments: false, quick_songs: false, marketplace: false, loaded: true });
      });
    return () => {
      alive = false;
    };
  }, []);

  return <PlatformFlagsContext.Provider value={state}>{children}</PlatformFlagsContext.Provider>;
};

/** Read the current platform module flags (plus `loaded`). */
export function usePlatformFlags(): PlatformFlagsState {
  return useContext(PlatformFlagsContext);
}

/** HOC: injects `platformFlags` into class components that cannot use hooks directly. */
export function withPlatformFlags<P extends object>(
  Wrapped: React.ComponentType<P & { platformFlags: PlatformFlagsState }>
): React.FC<P> {
  return function WithPlatformFlags(props: P) {
    const platformFlags = usePlatformFlags();
    return <Wrapped {...props} platformFlags={platformFlags} />;
  };
}

/**
 * Route guard for a single module. While flags are still loading it renders nothing
 * (avoids flashing a redirect); once loaded, it redirects to home if the module is
 * disabled, otherwise renders the protected route.
 */
export const ModuleGuard: React.FC<{ flag: keyof PlatformFlags; children: React.ReactNode }> = ({
  flag,
  children,
}) => {
  const flags = usePlatformFlags();
  if (!flags.loaded) return null;
  if (!flags[flag]) return <Navigate to="/" replace />;
  return <>{children}</>;
};
